import { memo } from 'react';
import { Box } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { primarySoft } from '../../../../../theme';
import {
    CHANNEL_PRIORITY_FIELD_NAME,
    DUAL_LIST_FIELD_NAMES,
    FIELD_GRID_POSITION,
    OPTIN_TO_STATUS_FIELD_NAME,
    PURCHASE_PERIOD_FIELD_NAME,
    ROW_SPAN_2_FIELD_NAMES,
} from './fieldConfig';
import { FieldRowProps } from './rowProps';
import OptinStatusRow from './OptinStatusRow';
import PurchaseActivityRow from './PurchaseActivityRow';
import ChannelPriorityRow from './ChannelPriorityRow';
import DualListRow from './DualListRow';
import DefaultRow from './DefaultRow';

const ROW_SX = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    p: 1.5,
    bgcolor: 'background.paper',
    borderRadius: 1,
    border: '1px solid', borderColor: 'divider',
    '&:hover': { borderColor: 'primary.main', boxShadow: (t: Theme) => `0 2px 8px ${primarySoft(t, 0.1)}` },
} as const;

function gridPlacementSx(fieldName: string) {
    const isRowSpan2 = ROW_SPAN_2_FIELD_NAMES.has(fieldName);
    const position = FIELD_GRID_POSITION[fieldName];
    if (position) {
        return {
            gridColumn: { md: position.column },
            gridRow: { md: isRowSpan2 ? `${position.row} / span 2` : `${position.row}` },
            alignSelf: isRowSpan2 ? 'stretch' : undefined,
        };
    }
    return isRowSpan2 ? { gridRow: 'span 2', alignSelf: 'stretch' } : {};
}

function FieldRowContent(props: FieldRowProps) {
    const { field, pairedField } = props;
    if (pairedField && field.name in OPTIN_TO_STATUS_FIELD_NAME) return <OptinStatusRow {...props} pairedField={pairedField} />;
    if (pairedField && field.name === PURCHASE_PERIOD_FIELD_NAME) return <PurchaseActivityRow {...props} pairedField={pairedField} />;
    if (field.name === CHANNEL_PRIORITY_FIELD_NAME) return <ChannelPriorityRow {...props} />;
    if (DUAL_LIST_FIELD_NAMES.has(field.name)) return <DualListRow {...props} />;
    return <DefaultRow {...props} />;
}

function FieldRow(props: FieldRowProps) {
    return (
        <Box sx={{ ...ROW_SX, ...gridPlacementSx(props.field.name) }}>
            <FieldRowContent {...props} />
        </Box>
    );
}

export default memo(FieldRow);
